/**
 * Flota — panel WWW (`fleet.web.*`), doklejone do słownika przez `extra.ts` (`fleet: { web: … }`).
 * Statusy, typy, błędy API i nazwy menu (`fleet.nav.*`, `fleet.errors.*`…) są w `@nexops/shared`.
 * `fleetWebEn` jest typowany względem `fleetWebPl` — brak klucza = błąd kompilacji.
 */
export type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetWebPl = {
  errors: {
    invalid: 'Nieprawidłowe dane: {details}',
    invalidShort: 'Nieprawidłowe dane — sprawdź formularz.',
    loadFailed: 'Nie udało się wczytać danych.',
    numberInvalid: 'Podaj poprawne liczby (bez ułamków tam, gdzie wymagane są całkowite).',
    required: 'Uzupełnij wszystkie wymagane pola.',
  },
  vehicles: {
    description: 'Pojazdy floty, ich stan licznika, kierowcy i wykorzystanie limitu leasingu.',
    add: 'Dodaj pojazd',
    searchPlaceholder: 'Szukaj po numerze rejestracyjnym, marce, modelu…',
    filterStatus: 'Status pojazdu',
    filterDriver: 'Kierowca',
    allStatuses: 'Wszystkie statusy',
    allDrivers: 'Wszyscy kierowcy',
    chipTotal: 'Pojazdy',
    empty: 'Brak pojazdów we flocie.',
    emptyFiltered: 'Żaden pojazd nie pasuje do filtrów.',
    noDriver: 'Brak kierowcy',
    dueOverdue: 'Termin minął',
    dueSoon: 'Termin w ciągu 30 dni',
    col: {
      vehicle: 'Pojazd',
      status: 'Status',
      driver: 'Kierowca',
      odometer: 'Przebieg',
      lease: 'Limit leasingu',
      inspection: 'Przegląd',
      insurance: 'Ubezpieczenie',
    },
  },
  vehicleForm: {
    createTitle: 'Nowy pojazd',
    editTitle: 'Edycja pojazdu',
    plate: 'Numer rejestracyjny',
    plateRequired: 'Podaj numer rejestracyjny.',
    make: 'Marka',
    model: 'Model',
    year: 'Rok produkcji',
    type: 'Typ pojazdu',
    status: 'Status',
    vin: 'VIN',
    initialOdometer: 'Stan licznika (km)',
    initialOdometerHint: 'Zapisywany jako pierwszy odczyt licznika.',
    group: 'Grupa kierowców',
    noGroup: 'Bez grupy',
    inspectionDueAt: 'Termin przeglądu',
    insuranceDueAt: 'Termin ubezpieczenia',
    notes: 'Uwagi',
    created: 'Dodano pojazd.',
    updated: 'Zapisano zmiany pojazdu.',
  },
  groups: {
    title: 'Grupy kierowców',
    description: 'Grupy porządkują pojazdy i kierowców (np. dostawy, handlowcy).',
    name: 'Nazwa grupy',
    descriptionField: 'Opis (opcjonalnie)',
    add: 'Dodaj grupę',
    edit: 'Zmień grupę',
    activate: 'Aktywuj',
    deactivate: 'Wyłącz',
    inactive: 'Nieaktywna',
    vehicleCount: 'Pojazdów: {count}',
    empty: 'Nie ma jeszcze żadnej grupy.',
    saved: 'Zapisano grupę.',
  },
  detail: {
    edit: 'Edytuj pojazd',
    vehicleData: 'Dane pojazdu',
    driverAndGroup: 'Kierowca i grupa',
    currentAssignment: 'Aktualne przypisanie',
    noAssignment: 'Brak aktywnego przypisania',
    since: 'od {date}',
    openLogbook: 'Otwarta karta drogowa',
    noOpenLogbook: 'Brak',
    activeLease: 'Aktywna umowa leasingowa',
    routes: 'Powiązane trasy',
    leasesHint: 'Umowy leasingowe pojazdu wraz z wykorzystaniem limitu kilometrów.',
    recentLogbooks: 'Ostatnie karty drogowe',
    recentLogbooksHint: 'Pięć ostatnich kart tego pojazdu.',
    allLogbooks: 'Wszystkie karty pojazdu',
    noLogbooks: 'Ten pojazd nie ma jeszcze kart drogowych.',
    tabs: {
      data: 'Dane',
      odometer: 'Przebiegi',
      logbooks: 'Karty drogowe',
      leases: 'Umowy leasingowe',
      assignments: 'Przypisania',
      attachments: 'Załączniki',
    },
    fields: {
      plate: 'Numer rejestracyjny',
      vin: 'VIN',
      make: 'Marka i model',
      year: 'Rok produkcji',
      type: 'Typ',
      status: 'Status',
      odometer: 'Stan licznika',
      inspection: 'Przegląd do',
      insurance: 'Ubezpieczenie do',
      defaultDriver: 'Domyślny kierowca',
      group: 'Grupa kierowców',
      notes: 'Uwagi',
      createdAt: 'Dodano',
    },
  },
  odometer: {
    title: 'Historia odczytów licznika',
    current: 'Aktualny stan: {km}. Odczyty są jedynym źródłem przebiegu.',
    add: 'Dodaj odczyt',
    correct: 'Korekta licznika',
    empty: 'Brak odczytów licznika.',
    col: { date: 'Data odczytu', value: 'Stan licznika', source: 'Źródło', details: 'Szczegóły' },
    correction: 'Korekta',
    replacement: 'Wymiana licznika',
    addTitle: 'Nowy odczyt licznika',
    addDescription: 'Ostatni odczyt: {km}. Wartość nie może być mniejsza.',
    valueKm: 'Stan licznika (km)',
    source: 'Źródło odczytu',
    note: 'Notatka (opcjonalnie)',
    saved: 'Zapisano odczyt licznika.',
    correctTitle: 'Korekta lub wymiana licznika',
    correctDescription: 'Obecny stan: {km}. Operacja trafia do dziennika audytu.',
    newValueKm: 'Nowy stan licznika (km)',
    reason: 'Powód',
    reasonHint: 'Wymagany — opisz, dlaczego stan jest zmieniany.',
    isReplacement: 'Wymiana licznika',
    isReplacementHint: 'Nowy licznik zaczyna od innej wartości; przebieg przed i po liczony osobno.',
    previousValue: 'Ostatni stan starego licznika (km)',
    previousHint: 'Puste = ostatni odczyt pojazdu.',
    correctSubmit: 'Zapisz korektę',
    corrected: 'Zapisano korektę licznika.',
  },
  assignments: {
    title: 'Przypisania kierowców',
    description: 'Historia przypisań — kierowca widzi pojazd w okresie przypisania.',
    assign: 'Przypisz kierowcę',
    assignTitle: 'Przypisanie kierowcy',
    assignDescription: 'Trwające przypisanie bezterminowe zostanie zamknięte dzień przed startem nowego.',
    driver: 'Kierowca',
    selectDriver: 'Wybierz kierowcę',
    noDrivers: 'Brak kont z rolą kierowcy.',
    validFrom: 'Od',
    validTo: 'Do (opcjonalnie)',
    note: 'Notatka (opcjonalnie)',
    current: 'Aktualne',
    ongoing: 'bezterminowo',
    close: 'Zakończ',
    closeTitle: 'Zakończ przypisanie',
    closeDescription: 'Przypisanie kierowcy {name} zostanie zamknięte w wybranym dniu.',
    closeDate: 'Ostatni dzień przypisania',
    empty: 'Pojazd nie ma jeszcze przypisań.',
    saved: 'Zapisano przypisanie.',
    closed: 'Zakończono przypisanie.',
    col: { driver: 'Kierowca', from: 'Od', to: 'Do', note: 'Notatka' },
  },
  attachments: {
    title: 'Załączniki umowy',
    upload: 'Dodaj plik',
    empty: 'Brak załączników.',
    noLeases: 'Załączniki są przypisane do umów leasingowych — ten pojazd nie ma jeszcze umowy.',
    download: 'Pobierz {name}',
    delete: 'Usuń {name}',
    deleteConfirm: 'Usunąć załącznik „{name}”?',
    uploaded: 'Dodano załącznik.',
    deleted: 'Usunięto załącznik.',
  },
  leases: {
    description: 'Umowy leasingowe i wykorzystanie limitu kilometrów — ostrzeżenia przy progach, prognoza do końca umowy.',
    add: 'Dodaj umowę',
    edit: 'Edytuj umowę',
    open: 'Otwórz umowę',
    openVehicle: 'Karta pojazdu',
    searchPlaceholder: 'Szukaj po numerze umowy, leasingodawcy, rejestracji…',
    filterStatus: 'Status umowy',
    filterWarning: 'Ostrzeżenie',
    allStatuses: 'Wszystkie statusy',
    allWarnings: 'Wszystkie poziomy',
    anyWarning: 'Dowolne ostrzeżenie',
    chipTotal: 'Umowy',
    empty: 'Brak umów leasingowych.',
    emptyFiltered: 'Żadna umowa nie pasuje do filtrów.',
    noneOnVehicle: 'Ten pojazd nie ma umowy leasingowej.',
    limit: 'limit {km}',
    usageTitle: 'Wykorzystanie limitu',
    usageDescription: 'Przebieg = różnica odczytów licznika od startu umowy.',
    contractTitle: 'Dane umowy',
    period: 'Okres umowy',
    noRate: 'brak stawki',
  },
  usage: {
    usedPercent: 'Wykorzystano {percent}% limitu',
    thresholdsCaption: 'progi {values}%',
    annual: 'Limit roczny (od ostatniej rocznicy umowy)',
    remaining: 'Pozostały limit',
    exceededBy: 'przekroczono o {km}',
    avgMonthly: 'Średnio miesięcznie',
    perMonth: 'mies.',
    daysRemaining: 'Do końca umowy',
    daysLeft: '{days} dni (do {date})',
    forecastEnd: 'Prognozowany przebieg na koniec umowy',
    forecastOverage: 'Prognozowane przekroczenie',
    forecastCost: 'Prognozowany koszt przekroczenia',
    costUnknown: 'nieznany — brak stawki za km',
    noForecast: 'brak danych do prognozy',
    withinLimit: 'mieści się w limicie',
    indicative: 'Prognoza orientacyjna',
    indicativeShort: 'orientacyjna',
    indicativeHint: 'za mało danych (krótki okres lub niski przebieg), wynik może się mocno zmienić.',
    noMetrics: 'Brak obliczeń limitu dla tej umowy (umowa nieaktywna).',
  },
  leaseForm: {
    createTitle: 'Nowa umowa leasingowa',
    editTitle: 'Edycja umowy leasingowej',
    vehicle: 'Pojazd',
    selectVehicle: 'Wybierz pojazd',
    lessor: 'Leasingodawca',
    contractNumber: 'Numer umowy',
    startDate: 'Początek umowy',
    endDate: 'Koniec umowy',
    status: 'Status umowy',
    totalLimitKm: 'Limit całkowity (km)',
    annualLimitKm: 'Limit roczny (km, opcjonalnie)',
    startOdometerKm: 'Przebieg początkowy (km)',
    startOdometerHint: 'Puste = bieżący stan licznika pojazdu.',
    overageRate: 'Stawka za przekroczony km',
    overageRateHint: 'Bez stawki koszt przekroczenia nie jest liczony.',
    monthlyFee: 'Rata miesięczna',
    currency: 'Waluta',
    thresholds: 'Progi ostrzegawcze (%)',
    thresholdN: 'Próg {n}',
    thresholdsHint: 'Trzy rosnące wartości 1–100, domyślnie z ustawień floty (80 / 90 / 100).',
    settlementRules: 'Zasady rozliczenia',
    notes: 'Uwagi',
    attachment: 'Załącznik (opcjonalnie)',
    attachmentHint: 'Skan lub plik umowy — kolejne pliki dodasz w widoku umowy.',
    created: 'Dodano umowę leasingową.',
    updated: 'Zapisano zmiany umowy.',
  },
  logbooks: {
    description: 'Kolejka kart drogowych do weryfikacji: zatwierdzenie, zwrot do poprawy albo zgłoszenie niezgodności.',
    mineDescription: 'Twoje karty drogowe — podgląd. Karty prowadzisz w aplikacji mobilnej.',
    chipTotal: 'Karty',
    searchPlaceholder: 'Szukaj po rejestracji, kierowcy, celu…',
    filterStatus: 'Status karty',
    filterVehicle: 'Pojazd',
    filterDriver: 'Kierowca',
    filterPeriod: 'Okres (miesiąc)',
    allStatuses: 'Wszystkie statusy',
    allVehicles: 'Wszystkie pojazdy',
    allDrivers: 'Wszyscy kierowcy',
    queue: 'Do weryfikacji',
    onlyFlagged: 'Tylko z flagami',
    empty: 'Brak kart drogowych.',
    emptyFiltered: 'Żadna karta nie pasuje do filtrów — kolejka do weryfikacji jest pusta.',
    planned: 'plan {km}',
    col: { date: 'Data', vehicle: 'Pojazd', driver: 'Kierowca', purpose: 'Cel', km: 'Przebieg', flags: 'Flagi', status: 'Status' },
  },
  logbookDetail: {
    summary: 'Podsumowanie karty',
    history: 'Historia zdarzeń',
    startKm: 'Licznik na starcie',
    endKm: 'Licznik na końcu',
    actualKm: 'Przebieg rzeczywisty',
    plannedKm: 'Przebieg planowany',
    startedAt: 'Rozpoczęto',
    endedAt: 'Zakończono',
    submittedAt: 'Wysłano do weryfikacji',
    flags: 'Flagi',
    noFlags: 'brak',
    driverNotes: 'Uwagi kierowcy',
    reviewComment: 'Komentarz weryfikującego',
    comment: 'Komentarz',
    optional: 'opcjonalnie',
    readOnlyHint: 'Podgląd własnej karty. Zmiany wprowadzasz w aplikacji mobilnej.',
    correct: 'Korekta licznika',
    correctTitle: 'Korekta stanu licznika na końcu karty',
    correctDescription: 'Obecny stan końcowy: {km}. Zmiana trafia do audytu i historii karty.',
    newEndKm: 'Nowy stan licznika na końcu (km)',
    corrected: 'Zapisano korektę odczytu karty.',
    dialog: {
      approve: 'Karta zostanie zatwierdzona. Komentarz jest opcjonalny.',
      return: 'Karta wróci do kierowcy do poprawy. Komentarz jest wymagany.',
      discrepancy: 'Karta zostanie oznaczona jako niezgodna. Komentarz jest wymagany.',
    },
    done: {
      approve: 'Karta zatwierdzona.',
      return: 'Karta zwrócona do poprawy.',
      discrepancy: 'Karta oznaczona jako niezgodna.',
    },
  },
  settings: {
    description: 'Progi ostrzegawcze limitów leasingu, minimum danych do prognozy i limity kart drogowych.',
    thresholdsTitle: 'Progi ostrzegawcze leasingu',
    thresholdsDescription: 'Trzy rosnące progi wykorzystania limitu km (domyślnie 80 / 90 / 100%). Dotyczą nowych umów.',
    thresholdN: 'Próg {n}',
    forecastTitle: 'Minimum danych do prognozy',
    forecastDescription: 'Poniżej tych wartości prognoza leasingu jest oznaczana jako orientacyjna.',
    minDays: 'Minimum dni umowy',
    minKm: 'Minimum przejechanych km',
    logbooksTitle: 'Karty drogowe',
    logbooksDescription: 'Limity służą do automatycznych flag na kartach (przebieg ponad plan lub ponad dzienne maksimum).',
    maxLogbookKm: 'Maksymalny przebieg karty (km)',
    tolerance: 'Tolerancja planu (%)',
    rangeError: 'Wartość poza dozwolonym zakresem (dni 1–365, km 0–100 000, maks. przebieg karty 100–10 000, tolerancja 0–200%).',
    reset: 'Przywróć zapisane',
    saved: 'Zapisano ustawienia floty.',
  },
};
